<template>
  <nav
    class="app-sidebar"
    :class="{
      'app-sidebar--collapsed': isCollapsed,
      'app-sidebar--overlay': isMobile && isOverlayOpen,
    }"
  >
    <button class="app-sidebar__toggle" @click="toggle" aria-label="Toggle sidebar">
      <span class="app-sidebar__toggle-icon">{{ isCollapsed ? '▶' : '◀' }}</span>
    </button>

    <ul class="app-sidebar__nav" role="navigation" aria-label="Main navigation">
      <li>
        <router-link
          to="/"
          class="app-sidebar__link"
          :class="{ 'app-sidebar__link--active': currentView === 'home' }"
          @click="handleLinkClick('home')"
        >
          <span class="app-sidebar__icon"><i class="pi pi-home"></i></span>
          <span v-if="!isCollapsed" class="app-sidebar__label">Home</span>
        </router-link>
      </li>

      <li v-if="canAccessImport">
        <router-link
          to="/import"
          class="app-sidebar__link"
          :class="{ 'app-sidebar__link--active': currentView === 'import' }"
          @click="handleLinkClick('import')"
        >
          <span class="app-sidebar__icon"><i class="pi pi-file-import"></i></span>
          <span v-if="!isCollapsed" class="app-sidebar__label">Import Services</span>
        </router-link>
      </li>

      <li v-if="canAccessWizard">
        <router-link
          to="/wizard"
          class="app-sidebar__link"
          :class="{ 'app-sidebar__link--active': currentView === 'wizard' }"
          @click="handleLinkClick('wizard')"
        >
          <span class="app-sidebar__icon"><i class="pi pi-bolt"></i></span>
          <span v-if="!isCollapsed" class="app-sidebar__label">Mapping Wizard</span>
        </router-link>
      </li>

      <li v-if="canAccessOnboarding">
        <router-link
          to="/onboarding-copy"
          class="app-sidebar__link"
          :class="{ 'app-sidebar__link--active': currentView === 'onboarding-copy' }"
          @click="handleLinkClick('onboarding-copy')"
        >
          <span class="app-sidebar__icon"><i class="pi pi-copy"></i></span>
          <span v-if="!isCollapsed" class="app-sidebar__label">Copy Product Mappings</span>
        </router-link>
      </li>

      <li v-if="canAccessMappingValidation">
        <router-link
          to="/mapping-validation"
          class="app-sidebar__link"
          :class="{ 'app-sidebar__link--active': currentView === 'mapping-validation' }"
          @click="handleLinkClick('mapping-validation')"
        >
          <span class="app-sidebar__icon"><i class="pi pi-check-circle"></i></span>
          <span v-if="!isCollapsed" class="app-sidebar__label">Mapping Validation</span>
        </router-link>
      </li>

      <li>
        <router-link
          to="/function-test"
          class="app-sidebar__link"
          :class="{ 'app-sidebar__link--active': currentView === 'function-test' }"
          @click="handleLinkClick('function-test')"
        >
          <span class="app-sidebar__icon"><i class="pi pi-code"></i></span>
          <span v-if="!isCollapsed" class="app-sidebar__label">Function Test</span>
        </router-link>
      </li>

      <li v-if="entities.length > 0">
        <button
          class="app-sidebar__link app-sidebar__tables-toggle"
          :class="{ 'app-sidebar__link--active': isEntityView }"
          :aria-expanded="tablesOpen"
          aria-controls="tables-dropdown"
          @click="toggleTables"
        >
          <span class="app-sidebar__icon"><i class="pi pi-table"></i></span>
          <span v-if="!isCollapsed" class="app-sidebar__label app-sidebar__tables-label">
            Tables
            <span v-if="isEntityView && selectedEntityName" class="app-sidebar__tables-current">{{ selectedEntityName }}</span>
            <i class="pi app-sidebar__tables-chevron" :class="tablesOpen ? 'pi-chevron-up' : 'pi-chevron-down'" />
          </span>
        </button>

        <div v-if="tablesOpen && !isCollapsed" id="tables-dropdown" class="app-sidebar__tables-panel">
          <div class="app-sidebar__tables-search">
            <input
              v-model="tableSearch"
              type="text"
              placeholder="Filter tables…"
              aria-label="Filter tables"
              class="app-sidebar__search-input"
            />
          </div>
          <ul class="app-sidebar__tables-list" role="listbox" aria-label="Available tables">
            <li v-for="entity in filteredTableEntities" :key="entity.name">
              <router-link
                :to="{ name: 'entity-manager', params: { entityName: entity.name } }"
                class="app-sidebar__tables-item"
                :class="{ 'app-sidebar__tables-item--active': currentView === entity.name }"
                role="option"
                :aria-selected="currentView === entity.name"
                @click="handleTableSelect(entity.name)"
              >
                {{ entity.name }}
              </router-link>
            </li>
            <li v-if="filteredTableEntities.length === 0" class="app-sidebar__tables-empty">
              No tables match
            </li>
          </ul>
        </div>
      </li>
    </ul>
  </nav>

  <div v-if="isMobile && isOverlayOpen" class="app-sidebar__backdrop" @click="closeOverlay" />
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useSidebarState } from '../../composables/useSidebarState';

interface Entity {
  name: string;
  fields?: Record<string, unknown>[];
}

const props = defineProps<{
  entities: Entity[];
  currentView: string;
  canAccessImport: boolean;
  canAccessWizard: boolean;
  canAccessOnboarding: boolean;
  canAccessMappingValidation: boolean;
}>();

const emit = defineEmits<{
  (e: 'navigate', view: string): void;
}>();

const { isCollapsed, isMobile, isOverlayOpen, toggle, closeOverlay } = useSidebarState();

const tablesOpen = ref(false);
const tableSearch = ref('');

const sortedEntities = computed(() =>
  [...props.entities].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
);

const filteredTableEntities = computed(() => {
  const q = tableSearch.value.toLowerCase();
  return q ? sortedEntities.value.filter((e) => e.name.toLowerCase().includes(q)) : sortedEntities.value;
});

const isEntityView = computed(() => props.entities.some((e) => e.name === props.currentView));
const selectedEntityName = computed(() => (isEntityView.value ? props.currentView : ''));

watch(
  () => props.currentView,
  (view) => {
    if (props.entities.some((e) => e.name === view)) {
      tablesOpen.value = true;
    }
  },
  { immediate: true }
);

function toggleTables(): void {
  if (isCollapsed.value) {
    toggle();
    tablesOpen.value = true;
    return;
  }
  tablesOpen.value = !tablesOpen.value;
}

function handleTableSelect(entityName: string): void {
  emit('navigate', entityName);
  if (isMobile.value && isOverlayOpen.value) {
    closeOverlay();
  }
}

function handleLinkClick(view: string): void {
  emit('navigate', view);
  if (isMobile.value && isOverlayOpen.value) {
    closeOverlay();
  }
}
</script>

<style scoped>
.app-sidebar {
  width: 240px;
  height: 100vh;
  position: sticky;
  top: 0;
  background: var(--color-surface);
  border-right: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  transition: width 0.2s ease;
  z-index: 100;
}

.app-sidebar--collapsed {
  width: 56px;
}

/* Toggle button */
.app-sidebar__toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-2);
  margin: var(--space-2);
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  cursor: pointer;
  color: var(--color-text-primary);
  font-size: var(--text-base);
}

.app-sidebar__toggle:hover {
  background: var(--color-surface-raised);
}

.app-sidebar__toggle-icon {
  display: inline-block;
}

.app-sidebar__search-input {
  width: 100%;
  padding: var(--space-2);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  background: var(--input-bg);
  color: var(--color-text-primary);
  font-size: var(--text-sm);
  box-sizing: border-box;
}

.app-sidebar__search-input::placeholder {
  color: var(--color-text-muted);
}

/* Navigation list */
.app-sidebar__nav {
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1;
}

.app-sidebar__nav li {
  margin: 0;
  padding: 0;
}

/* Links */
.app-sidebar__link {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  text-decoration: none;
  color: var(--color-text-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  border-left: 3px solid transparent;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.app-sidebar__link:hover {
  background: var(--color-surface-raised);
}

.app-sidebar__link--active {
  background: color-mix(in srgb, var(--color-primary) 15%, transparent);
  color: var(--color-primary);
  border-left: 3px solid var(--color-primary);
}

.app-sidebar__icon {
  flex-shrink: 0;
  font-size: var(--text-base);
  width: 24px;
  text-align: center;
}

.app-sidebar__label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.app-sidebar__tables-toggle {
  width: 100%;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
}

.app-sidebar__tables-label {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  flex: 1;
  overflow: hidden;
}

.app-sidebar__tables-current {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--color-text-muted);
  font-size: var(--text-xs, 0.75rem);
  font-weight: var(--font-normal);
}

.app-sidebar__tables-chevron {
  margin-left: auto;
  font-size: var(--text-xs, 0.75rem);
  flex-shrink: 0;
}

.app-sidebar__tables-panel {
  background: var(--color-surface-raised, var(--color-surface));
  border-top: 1px solid var(--border-color);
  border-bottom: 1px solid var(--border-color);
}

.app-sidebar__tables-search {
  padding: var(--space-2);
}

.app-sidebar__tables-list {
  list-style: none;
  margin: 0;
  padding: 0 0 var(--space-1);
  max-height: 320px;
  overflow-y: auto;
}

.app-sidebar__tables-item {
  display: block;
  padding: var(--space-1) var(--space-4);
  text-decoration: none;
  color: var(--color-text-primary);
  font-size: var(--text-sm);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: background 0.1s ease;
}

.app-sidebar__tables-item:hover {
  background: color-mix(in srgb, var(--color-primary) 8%, transparent);
}

.app-sidebar__tables-item--active {
  background: color-mix(in srgb, var(--color-primary) 15%, transparent);
  color: var(--color-primary);
  font-weight: var(--font-medium);
}

.app-sidebar__tables-empty {
  padding: var(--space-2) var(--space-4);
  color: var(--color-text-muted);
  font-size: var(--text-sm);
  font-style: italic;
}

/* Mobile overlay */
@media (max-width: 767px) {
  .app-sidebar {
    position: fixed;
    left: -240px;
    z-index: 1100;
  }

  .app-sidebar--overlay {
    left: 0;
    width: 240px;
    box-shadow: var(--shadow-lg);
  }
}

/* Backdrop */
.app-sidebar__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1099;
}
</style>
